import { centsToDollars, isLiveOneTime } from "@outschool/business-rules";
import { createUrlWithActivity } from "@outschool/routes";
import type {
  Activity,
  ActivityListing,
  ActivityListings,
  GiftCard,
  Product,
  Products,
} from "./types";

export function mapActivityListingsToProducts(
  listings: ActivityListings,
): Products {
  return listings.map((listing: ActivityListing, index: number) =>
    mapActivityListingToProduct(listing, index),
  );
}

export function mapActivityListingToProduct(
  listing: ActivityListing,
  index: number = 0,
): Product {
  return {
    product_id: listing.activity.slug_id,
    sku: listing.activity.uid,
    category: listing.activity.subject ?? undefined,
    name: listing.activity.title,
    brand: listing.leader?.name ?? undefined,
    variant: getActivityFormat(listing.activity),
    price:
      listing.activity.price_cents === null
        ? undefined
        : listing.activity.price_cents / 100,
    quantity: 1,
    position: index,
    url: createUrlWithActivity(listing.activity),
    image_url: listing.activity?.details?.photo ?? undefined,
  };
}

export function mapGiftCardToProduct(giftCard: GiftCard): Product {
  return {
    brand: "Outschool",
    category: "gift",
    image_url: "",
    name: "Gift Card",
    position: 0,
    price: centsToDollars(giftCard.amountCents),
    product_id: "gift-card",
    quantity: 1,
    sku: giftCard.uid || "",
    url: giftCard.url,
    variant: "gift-card",
  };
}

enum ActivityFormat {
  Flex = "Flexible Schedule",
  Ongoing = "Ongoing",
  OneTime = "One-Time",
  MultiDay = "Multi-Day",
  Club = "Group",
}

function getActivityFormat(activity: Activity): ActivityFormat {
  if (activity.isClub || activity.is_club) {
    return ActivityFormat.Club;
  }

  if (isFlexSchedule(activity)) {
    return ActivityFormat.Flex;
  }

  if (!!activity.is_ongoing_weekly) {
    return ActivityFormat.Ongoing;
  }

  if (isLiveOneTime(activity)) {
    return ActivityFormat.OneTime;
  }

  return ActivityFormat.MultiDay;
}

function isFlexSchedule(
  activity: Pick<Activity, "weekly_meetings" | "duration_weeks">,
) {
  return !activity?.weekly_meetings && !!activity?.duration_weeks;
}
